import type { QueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
} from 'react-router-dom';
import type { PermissionKey } from '@lims/permissions';
import { PERMISSIONS } from '@lims/permissions';
import { AppShell } from './AppShell.js';
import { Providers } from './Providers.js';
import { AuthProvider, useAuth } from './auth-context.js';
import { LoadingState } from '../components/ui/index.js';
import { ForgotPasswordPage } from '../modules/auth/ForgotPasswordPage.js';
import { LoginPage } from '../modules/auth/LoginPage.js';
import { ResetPasswordPage } from '../modules/auth/ResetPasswordPage.js';
import { AuditPage } from '../modules/audit/AuditPage.js';
import { CataloguePage } from '../modules/catalogue/CataloguePage.js';
import { CatalogueItemPage } from '../modules/catalogue/CatalogueItemPage.js';
import { ClientContactsPage } from '../modules/clients/ClientContactsPage.js';
import { ClientFormPage } from '../modules/clients/ClientFormPage.js';
import { ClientFilePage } from '../modules/clients/ClientFilePage.js';
import { ClientsPage } from '../modules/clients/ClientsPage.js';
import { ProjectFilePage } from '../modules/projects/ProjectFilePage.js';
import { ProjectFormPage } from '../modules/projects/ProjectFormPage.js';
import { ProjectsPage } from '../modules/projects/ProjectsPage.js';
import { PricingAgreementsPage } from '../modules/pricing/PricingAgreementsPage.js';
import { PricingAgreementFormPage } from '../modules/pricing/PricingAgreementFormPage.js';
import { BillingPage } from '../modules/finance/BillingPage.js';
import { InvoiceDetailPage } from '../modules/finance/InvoiceDetailPage.js';
import { InvoiceFormPage } from '../modules/finance/InvoiceFormPage.js';
import { InvoicesPage } from '../modules/finance/InvoicesPage.js';
import { PaymentDetailPage } from '../modules/finance/PaymentDetailPage.js';
import { PaymentFormPage } from '../modules/finance/PaymentFormPage.js';
import { PaymentsPage } from '../modules/finance/PaymentsPage.js';
import { StatementPage } from '../modules/finance/StatementPage.js';
import { QuotationsPage } from '../modules/quotations/QuotationsPage.js';
import { QuotationFormPage } from '../modules/quotations/QuotationFormPage.js';
import { QuotationPreviewPage } from '../modules/quotations/QuotationPreviewPage.js';
import { QuotationConvertPage } from '../modules/quotations/QuotationConvertPage.js';
import { SamplesPage } from '../modules/samples/SamplesPage.js';
import { SampleReceivePage } from '../modules/samples/SampleReceivePage.js';
import { SampleFilePage } from '../modules/samples/SampleFilePage.js';
import { SampleReceiptPage } from '../modules/samples/SampleReceiptPage.js';
import { SampleReceiptsPage } from '../modules/samples/SampleReceiptsPage.js';
import { SampleTypeSettingsPage } from '../modules/samples/SampleTypeSettingsPage.js';
import { WorkOrdersPage } from '../modules/work-orders/WorkOrdersPage.js';
import { WorkOrderFormPage } from '../modules/work-orders/WorkOrderFormPage.js';
import { WorkOrderFilePage } from '../modules/work-orders/WorkOrderFilePage.js';
import { DesignSystemPage } from '../modules/design-system/DesignSystemPage.js';
import { ProfilePage } from '../modules/profile/ProfilePage.js';
import { BranchesPage } from '../modules/settings/BranchesPage.js';
import { DepartmentsPage } from '../modules/settings/DepartmentsPage.js';
import { SettingsPage } from '../modules/settings/SettingsPage.js';
import { StoragePage } from '../modules/storage/StoragePage.js';
import { UserFormPage } from '../modules/users/UserFormPage.js';
import { UsersPage } from '../modules/users/UsersPage.js';
import { EquipmentPage } from '../modules/equipment/EquipmentPage.js';
import { ManagementDashboardPage } from '../modules/dashboards/ManagementDashboardPage.js';
import { ImportPage } from '../modules/imports/ImportPage.js';
import {
  MaintenanceListPage,
  MaintenanceRecordPage,
} from '../modules/maintenance/MaintenancePages.js';
import {
  NotificationSettingsPage,
  NotificationsPage,
} from '../modules/notifications/NotificationsPages.js';
import { CorrectiveActionsPage } from '../modules/quality/CorrectiveActionsPage.js';
import { QualityPage } from '../modules/quality/QualityPage.js';
import { QualityRecordPage } from '../modules/quality/QualityRecordPage.js';
import { EquipmentDetailPage } from '../modules/equipment/EquipmentDetailPage.js';
import { PortalApp } from '../portal/PortalApp.js';
import { SpecificationsPage } from '../modules/settings/SpecificationsPage.js';
import { ReportTemplateDetailPage } from '../modules/settings/ReportTemplateDetailPage.js';
import { ReportTemplatesPage } from '../modules/settings/ReportTemplatesPage.js';
import { ReportDetailPage } from '../modules/reports/ReportDetailPage.js';
import { ReportPreparePage } from '../modules/reports/ReportPreparePage.js';
import { ReportsPage } from '../modules/reports/ReportsPage.js';
import { VerificationPage } from '../modules/verification/VerificationPage.js';
import { AssignTestPage } from '../modules/work/AssignTestPage.js';
import { AssignmentFilePage } from '../modules/work/AssignmentFilePage.js';
import { AttemptPage } from '../modules/work/AttemptPage.js';
import { HomePage } from '../modules/work/HomePage.js';
import { MyTasksPage } from '../modules/work/MyTasksPage.js';
import { WorkListPage } from '../modules/work/WorkListPage.js';
import { ApprovalPage } from '../modules/work/ApprovalPage.js';
import { ApprovalQueuePage } from '../modules/work/ApprovalQueuePage.js';
import { ReturnedWorkPage } from '../modules/work/ReturnedWorkPage.js';
import { ReviewPage } from '../modules/work/ReviewPage.js';
import { ReviewQueuePage } from '../modules/work/ReviewQueuePage.js';

/**
 * Routing.
 *
 * Route guards decide what to SHOW. They are not the security boundary: every
 * page behind them calls an endpoint that independently re-checks the same
 * permission, so a user who edits the URL, or skips the interface entirely,
 * gets the same 401 or 403 they would have got here.
 */

function RequireAuth() {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingState label={t('common.loading')} />
      </div>
    );
  }

  return user ? <Outlet /> : <Navigate to="/login" replace />;
}

function RequirePermission({ permission }: { permission: PermissionKey }) {
  const { can } = useAuth();
  // Sent to their own profile rather than shown a dead end, since every
  // authenticated user can always reach that.
  return can(permission) ? <Outlet /> : <Navigate to="/profile" replace />;
}

/** Phase 12: the route opens for a caller holding ANY of the permissions —
 *  a person who may only raise a quality record reaches the raising form. */
function RequireAnyPermission({ permissions }: { permissions: readonly PermissionKey[] }) {
  const { can } = useAuth();
  return permissions.some((key) => can(key)) ? <Outlet /> : <Navigate to="/profile" replace />;
}

/**
 * Where "/" lands: the home dashboard for anybody who may see it, their own
 * profile otherwise. A redirect, not a gate — the dashboard endpoint checks the
 * permission itself.
 */
function HomeRedirect() {
  const { can } = useAuth();
  return (
    <Navigate to={can(PERMISSIONS.DASHBOARD_VIEW) ? '/home' : '/profile'} replace />
  );
}

export function App({ queryClient }: { queryClient: QueryClient }) {
  return (
    <BrowserRouter>
      <Providers queryClient={queryClient}>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            {/*
              Phase 10 — PUBLIC report verification (D122, D130). OUTSIDE
              sign-in and OUTSIDE the shell: whoever holds a printed report
              holds its link, and a signed-in member of staff sees exactly what
              a stranger sees, because the page sends no session with its
              requests. The laboratory comes from the token's answer, never
              from this installation or the host name.
            */}
            <Route path="/verify/:token" element={<VerificationPage />} />

            {/*
              Phase 11 — the CLIENT PORTAL (D146). Its own session, shell, API
              client and guard, outside the staff sign-in: a client's contact
              is never a member of staff, and neither session serves the other.
            */}
            <Route path="/portal/*" element={<PortalApp />} />

            <Route element={<RequireAuth />}>
              <Route element={<AppShell />}>
                <Route index element={<HomeRedirect />} />
                <Route path="/profile" element={<ProfilePage />} />

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.USERS_VIEW} />
                  }
                >
                  <Route path="/users" element={<UsersPage />} />
                  <Route path="/users/new" element={<UserFormPage />} />
                  <Route path="/users/:id" element={<UserFormPage />} />
                </Route>

                {/*
                  Clients and projects. The guard here decides what to SHOW;
                  every endpoint behind these pages re-checks the same
                  permission and the same laboratory on the server.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.CLIENTS_VIEW} />
                  }
                >
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/new" element={<ClientFormPage />} />
                  <Route path="/clients/:id" element={<ClientFilePage />} />
                  <Route
                    path="/clients/:id/edit"
                    element={<ClientFormPage />}
                  />
                  <Route
                    path="/clients/:id/contacts"
                    element={<ClientContactsPage />}
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.PROJECTS_VIEW} />
                  }
                >
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/new" element={<ProjectFormPage />} />
                  <Route path="/projects/:id" element={<ProjectFilePage />} />
                  <Route
                    path="/projects/:id/edit"
                    element={<ProjectFormPage />}
                  />
                </Route>

                {/*
                  Catalogue, quotations and pricing agreements. The guard here
                  decides what to SHOW; every endpoint behind these pages
                  re-checks the same permission and the same laboratory on the
                  server. A price change needs `pricing.override` on top,
                  which only the backend can enforce.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.CATALOGUE_VIEW} />
                  }
                >
                  <Route path="/catalogue" element={<CataloguePage />} />
                  <Route
                    path="/catalogue/new"
                    element={<CatalogueItemPage />}
                  />
                  <Route
                    path="/catalogue/:id"
                    element={<CatalogueItemPage />}
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={PERMISSIONS.QUOTATIONS_VIEW}
                    />
                  }
                >
                  <Route path="/quotations" element={<QuotationsPage />} />
                  <Route
                    path="/quotations/new"
                    element={<QuotationFormPage />}
                  />
                  <Route
                    path="/quotations/:id"
                    element={<QuotationPreviewPage />}
                  />
                  <Route
                    path="/quotations/:id/edit"
                    element={<QuotationFormPage />}
                  />
                  <Route
                    path="/quotations/:id/convert"
                    element={<QuotationConvertPage />}
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={PERMISSIONS.PRICING_AGREEMENTS_VIEW}
                    />
                  }
                >
                  <Route
                    path="/pricing-agreements"
                    element={<PricingAgreementsPage />}
                  />
                  <Route
                    path="/pricing-agreements/new"
                    element={<PricingAgreementFormPage />}
                  />
                  <Route
                    path="/pricing-agreements/:id"
                    element={<PricingAgreementFormPage />}
                  />
                </Route>

                {/*
                  Finance (Phase 11): billing, invoices and payments. The
                  guard here decides what to SHOW; every finance route
                  re-checks `finance.view` and the more specific act
                  (`invoices.issue`, `payments.adjust`, ...) on the server —
                  hiding a button here is never the security (CLAUDE.md 5.1).
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.FINANCE_VIEW} />
                  }
                >
                  <Route path="/billing" element={<BillingPage />} />
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/invoices/new" element={<InvoiceFormPage />} />
                  <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
                  <Route path="/payments" element={<PaymentsPage />} />
                  <Route path="/payments/new" element={<PaymentFormPage />} />
                  <Route path="/payments/:id" element={<PaymentDetailPage />} />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.STATEMENTS_VIEW} />
                  }
                >
                  <Route
                    path="/clients/:id/statement"
                    element={<StatementPage />}
                  />
                </Route>

                {/*
                  Work orders. The guard decides what to SHOW; every endpoint
                  behind these pages re-checks the same permission and resolves
                  the laboratory from the session. A work-order id belonging to
                  another laboratory returns 404 from the API, never 403 — a
                  403 would confirm that the record exists.
                */}
                <Route
                  element={
                    <RequirePermission
                      permission={PERMISSIONS.WORK_ORDERS_VIEW}
                    />
                  }
                >
                  <Route path="/work-orders" element={<WorkOrdersPage />} />
                  <Route
                    path="/work-orders/new"
                    element={<WorkOrderFormPage />}
                  />
                  <Route
                    path="/work-orders/:id"
                    element={<WorkOrderFilePage />}
                  />
                  <Route
                    path="/work-orders/:id/edit"
                    element={<WorkOrderFormPage />}
                  />
                </Route>

                {/*
                  Samples. The guard decides what to SHOW; every endpoint behind
                  these pages re-checks the same permission and resolves the
                  laboratory from the session. A sample id belonging to another
                  laboratory returns 404 from the API, never 403 — a 403 would
                  confirm that the record exists.

                  Receiving, deciding, printing a label and managing storage are
                  four SEPARATE permissions, and the pages behind this one guard
                  show their own controls accordingly.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.SAMPLES_VIEW} />
                  }
                >
                  <Route path="/samples" element={<SamplesPage />} />
                  <Route
                    path="/samples/receive"
                    element={<SampleReceivePage />}
                  />
                  {/* The register before the receipt, so the bare path is never an id (D213). */}
                  <Route
                    path="/samples/receipts"
                    element={<SampleReceiptsPage />}
                  />
                  <Route
                    path="/samples/receipts/:id"
                    element={<SampleReceiptPage />}
                  />
                  <Route path="/samples/:id" element={<SampleFilePage />} />
                </Route>

                {/*
                  Phase 8. Each guard is the permission the page's first read
                  needs; every action inside re-checks its own permission on
                  the server, which is the only place it means anything.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.DASHBOARD_VIEW} />
                  }
                >
                  <Route path="/home" element={<HomePage />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.ASSIGNMENTS_VIEW} />
                  }
                >
                  <Route path="/my-tasks" element={<MyTasksPage />} />
                  <Route path="/work" element={<WorkListPage />} />
                  <Route path="/work/assign" element={<AssignTestPage />} />
                  <Route path="/work/:id" element={<AssignmentFilePage />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.RESULTS_VIEW} />
                  }
                >
                  <Route path="/attempts/:id" element={<AttemptPage />} />
                </Route>

                {/*
                  Phase 9 — technical review and final approval. REVIEW AND
                  APPROVAL ARE SEPARATE PERMISSIONS and therefore separate
                  guards: holding one never opens the other, even for the same
                  person (decision D97).

                  As everywhere else, the guard decides what to SHOW. Each
                  endpoint behind these pages re-checks the permission, the
                  state and that the caller is not the performer, and a database
                  trigger refuses self-review and self-approval independently of
                  the API (D107). A person who edits the URL reaches the page
                  and gets a refusal from the server, which is where it counts.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.RESULTS_REVIEW} />
                  }
                >
                  <Route path="/review" element={<ReviewQueuePage />} />
                  <Route path="/review/:attemptId" element={<ReviewPage />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.RESULTS_APPROVE} />
                  }
                >
                  <Route path="/approval" element={<ApprovalQueuePage />} />
                  <Route
                    path="/approval/:attemptId"
                    element={<ApprovalPage />}
                  />
                </Route>
                {/* The performer's own returned work, resolved from the session. */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.RESULTS_ENTER} />
                  }
                >
                  <Route path="/returned" element={<ReturnedWorkPage />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.EQUIPMENT_VIEW} />
                  }
                >
                  <Route path="/equipment" element={<EquipmentPage />} />
                  <Route path="/equipment/:id" element={<EquipmentDetailPage />} />
                </Route>
                {/*
                  Phase 12 — quality, maintenance, notifications, the
                  management dashboard and import (D161–D178). As everywhere,
                  a guard decides what to SHOW; every endpoint behind these
                  pages re-checks its permission. `/maintenance/faults` is a
                  static segment and outranks `/maintenance/:id`.
                */}
                <Route
                  element={
                    <RequireAnyPermission
                      permissions={[PERMISSIONS.QUALITY_VIEW, PERMISSIONS.QUALITY_RAISE]}
                    />
                  }
                >
                  <Route path="/quality" element={<QualityPage />} />
                </Route>
                <Route
                  element={<RequirePermission permission={PERMISSIONS.QUALITY_VIEW} />}
                >
                  <Route path="/quality/:id" element={<QualityRecordPage />} />
                  <Route path="/corrective-actions" element={<CorrectiveActionsPage />} />
                </Route>
                <Route
                  element={<RequirePermission permission={PERMISSIONS.EQUIPMENT_VIEW} />}
                >
                  <Route path="/maintenance" element={<MaintenanceListPage kind="planned" />} />
                  <Route path="/maintenance/faults" element={<MaintenanceListPage kind="fault" />} />
                  <Route path="/maintenance/:id" element={<MaintenanceRecordPage />} />
                </Route>
                <Route
                  element={<RequirePermission permission={PERMISSIONS.DASHBOARD_VIEW} />}
                >
                  <Route path="/dashboards" element={<ManagementDashboardPage />} />
                </Route>
                <Route
                  element={<RequirePermission permission={PERMISSIONS.DATA_IMPORT} />}
                >
                  <Route path="/settings/import" element={<ImportPage />} />
                </Route>
                {/* A person's own notifications: authentication is enough. */}
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/notifications/settings" element={<NotificationSettingsPage />} />

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.CATALOGUE_VIEW} />
                  }
                >
                  <Route
                    path="/settings/specifications"
                    element={<SpecificationsPage />}
                  />
                </Route>

                {/*
                  Phase 10 — reports up to and including issuance. READING the
                  register and a report needs `reports.view`; PREPARING needs
                  `reports.prepare`, a separate guard, so holding one never opens
                  the other. `/reports/new` is a static segment and outranks
                  `/reports/:id`. Issuing, previewing and discarding are acts on
                  the report's own page, offered from the server's `can.*`
                  hints and re-checked by every endpoint; another laboratory's
                  report answers 404 from the API.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.REPORTS_VIEW} />
                  }
                >
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/reports/:id" element={<ReportDetailPage />} />
                </Route>
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.REPORTS_PREPARE} />
                  }
                >
                  <Route path="/reports/new" element={<ReportPreparePage />} />
                </Route>

                {/*
                  Phase 10 — the report-template register (D112). READING needs
                  `templates.view`; every change needs `templates.manage`,
                  which the endpoints re-check on every request. A template id
                  of another laboratory answers 404 from the API.
                */}
                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.TEMPLATES_VIEW} />
                  }
                >
                  <Route
                    path="/report-templates"
                    element={<ReportTemplatesPage />}
                  />
                  <Route
                    path="/report-templates/:id"
                    element={<ReportTemplateDetailPage />}
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.SETTINGS_VIEW} />
                  }
                >
                  <Route path="/settings" element={<SettingsPage />} />
                  {/*
                    The laboratory's own structure. Managing either list needs
                    its own permission on top, which only the backend can
                    enforce; READING them needs none, because the work-order
                    form needs the lists and raising a job does not make
                    anybody an administrator (decision D57).
                  */}
                  <Route path="/settings/branches" element={<BranchesPage />} />
                  <Route
                    path="/settings/departments"
                    element={<DepartmentsPage />}
                  />
                  {/*
                    The sample type builder (0024) and the per-type policy.
                    READING it needs only `settings.view`; changing a type or
                    its fields needs `settings.update`, a required-field rule
                    `samples.update` and a retention period
                    `sample_storage.manage`, all enforced by the backend.
                  */}
                  <Route
                    path="/settings/sample-types"
                    element={<SampleTypeSettingsPage />}
                  />
                </Route>

                <Route
                  element={
                    <RequirePermission
                      permission={PERMISSIONS.STORAGE_MANAGE}
                    />
                  }
                >
                  <Route path="/storage" element={<StoragePage />} />
                </Route>

                <Route
                  element={
                    <RequirePermission permission={PERMISSIONS.AUDIT_VIEW} />
                  }
                >
                  <Route path="/audit" element={<AuditPage />} />
                </Route>

                {/*
                  The design-system page is a DEVELOPMENT artefact. This is a
                  compile-time constant, so a production build removes the
                  route and tree-shakes the page module out of the bundle
                  entirely — it is not merely hidden behind a runtime check
                  that a crafted URL could reach.
                */}
                {import.meta.env.DEV ? (
                  <Route path="/design-system" element={<DesignSystemPage />} />
                ) : null}
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </AuthProvider>
      </Providers>
    </BrowserRouter>
  );
}
